import { createContext, type FunctionComponent, useContext } from "react";

import { type BuilderFlags, type BuilderInfo } from "helpers/AppProvider.types";

/**
 * Context containing builder info, including certain builder configurations, the
 * builder's locale, and the builder id.
 *
 * The context consumer value will be null if no builder is in session
 * or undefined if the context consumer is not within a parent
 */
export const BuilderInfoContext = createContext<BuilderInfo | null | undefined>(undefined);

export interface IBuilderInfoProviderProps {
    value: BuilderInfo | null;
    children: React.ReactNode;
}

export const BuilderInfoProvider: React.FC<IBuilderInfoProviderProps> = ({ value, children }) => {
    return (
        // eslint-disable-next-line react/forbid-elements
        <BuilderInfoContext.Provider value={value}>{children}</BuilderInfoContext.Provider>
    );
};

export interface IWhenBuilderFlagProps {
    /**
     * The builder flag key to render the "then" prop if true, or "otherwise" if false. You can pass
     * multiple flag conditions which are AND'D together.
     * @example
     * // Single condition
     * <WhenBuilderFlag
     *     isEnabled="myFlag"
     *     then={<>I display when myFlag is true</>}
     *  />
     * // Multiple conditions
     * <WhenBuilderFlag
     *     isEnabled={{ flag1: true, flag2: false }}
     *     then={<>I display when flag1 is true and flag2 is false</>}
     * />
     */
    isEnabled: keyof BuilderFlags | Partial<{ [key in keyof BuilderFlags]: boolean }>;
    /**
     * The react element or render function which will be outputed when the flag condition matches.
     * Will not render anything if not provided. **Avoid using the render function
     * when possible as this may be removed soon**
     */
    then?: React.ReactNode | (() => React.ReactNode);
    /**
     * The react element or render function which will be outputed when the flag condition **DOES
     * NOT** match. Will not render anything if not provided. **Avoid using the render function
     * when possible as this may be removed soon**
     */
    otherwise?: React.ReactNode | (() => React.ReactNode);
    /**
     * The default to render when no builder context is present
     * If not set, then this component will throw an error without context
     */
    noBuilderInfoContextDefault?: "then" | "otherwise";
}

/**
 * Utility that renders components depending on the flag value
 * @example
 * <WhenBuilderFlag
 *   isEnabled="myFlag"
 *   then={<>My flag is enabled</>}
 *   otherwise={<>My flag is disabled</>}
 * />
 */
export const WhenBuilderFlag: FunctionComponent<IWhenBuilderFlagProps> = ({
    isEnabled: flags,
    then,
    otherwise,
    noBuilderInfoContextDefault,
}) => {
    const builderContext = useContext(BuilderInfoContext);
    const renderThen = () => {
        if (typeof then === "function") {
            return <>{then()}</>;
        } else {
            return <>{then}</>;
        }
    };

    const renderOtherwise = () => {
        if (typeof otherwise === "function") {
            return <>{otherwise()}</>;
        } else {
            return <>{otherwise}</>;
        }
    };

    if (!builderContext) {
        if (noBuilderInfoContextDefault === "then" && then) {
            return renderThen();
        } else if (noBuilderInfoContextDefault === "otherwise" && otherwise) {
            return renderOtherwise();
        } else {
            throw new Error(
                "Attempted to enter builder flag check but the builder info is not in context. Did you " +
                    "forget to add an AppProvider or BuilderInfoContext wrapper? Is the builder in session?",
            );
        }
    } else {
        let areAllBuilderFlagsInCorrectState: boolean;
        if (typeof flags === "object") {
            areAllBuilderFlagsInCorrectState = Array.from(Object.entries(flags)).every(
                ([flagKey, desiredFlagValue]) =>
                    builderContext.flags[flagKey as keyof typeof builderContext.flags] ===
                    desiredFlagValue,
            );
        } else {
            areAllBuilderFlagsInCorrectState = builderContext.flags[flags];
        }

        if (areAllBuilderFlagsInCorrectState) {
            if (then) {
                return renderThen();
            }
        } else {
            if (otherwise) {
                return renderOtherwise();
            }
        }
    }

    return null;
};
